import { Component, Input, Output, EventEmitter, ViewChild, OnChanges, OnInit, SimpleChanges, HostListener } from '@angular/core';
import { DatatableComponent } from '@swimlane/ngx-datatable';
import { DataService } from '../../services/data/data.service';
import { DisplayService } from '../../services/display/display.service';
import { ConfigService } from '../../services/config/config.service';
import { SearchService } from '../../services/search/search.service';

@Component({
	selector: 'pcm-data-table',
	templateUrl: './data-table.component.html',
	styleUrls: ['./data-table.component.scss']
})

export class DataTableComponent implements OnChanges, OnInit {

	@ViewChild('tableWithCheckbox') tableWithCheckbox: DatatableComponent;
	@ViewChild('tableNoCheckbox') tableNoCheckbox: DatatableComponent;

	@Input() rowsData;
	@Input() columnsConfig;
	@Input() title;
	@Input() helpText;
	@Input() selectionType = 'single';
	@Input() sorts = [];
	@Input() smallThreshold = 1080;
	@Input() rowsPerPage = 15;
	// need to deselect the table
	@Input() selected = [];
	@Input() rowId = '_id';
	@Input() deselect = true;
	@Input() dataObj = '';
	@Input() tab: any = {};
	@Input() canCollapse = false;
	@Input() pageNumber = 0;
	@Input() searching = false;
	@Input() displayCheck;
	// display h scrollbar and total number
	@Input() advanced = false;
	@Input() sortable = true;

	// Display and allow the user to modify rowsPerPage
	@Input() showRowsPerPage = false;

	@Output() tableClick: EventEmitter<any> = new EventEmitter();
	@Output() tableSelect: EventEmitter<any> = new EventEmitter();
	@Output() tablePage: EventEmitter<any> = new EventEmitter();
	@Output() tableSort: EventEmitter<any> = new EventEmitter();
	@Output() tableSearch: EventEmitter<any> = new EventEmitter();
	@Output() tableRefresh: EventEmitter<any> = new EventEmitter();
	@Output() search: EventEmitter<any> = new EventEmitter();

	@HostListener('window:resize', ['$event'])
	onResize() {
		// this is a manual update to force a re-size update on the ngx-datatable component
		// without doing this, the columns resize, but the whole table itself doesn't resize when resizing the window
		this.tableWithCheckbox?.onColumnResize({ column: {}, newValue: {} });
		this.tableNoCheckbox?.onColumnResize({ column: {}, newValue: {} });
	}

	tabObj = '';
	columnsConfigSmall = [];
	activeMultis = -1;
	activeCode = -1;
	activeRow = -1;
	activeColumn = -1;
	collapsed = false;
	selectAllOnPage = [];

	constructor(
		public dataService: DataService,
		public configService: ConfigService,
		public displayService: DisplayService,
		public searchService: SearchService
	) { }

	ngOnInit() {
		if (this.deselect) {
			this.dataService.clearSelectedTableRows();
		}

		// this needs to happen on load, but also on any changes
		this.updateTableData();
	}

	ngOnChanges(changes: SimpleChanges) {
		// if tab name changed and selection should be cleared, clear selection
		if (this.deselect && changes['tab'] && changes['tab'].previousValue && changes['tab'].previousValue.prop !== changes['tab'].currentValue.prop) {
			this.dataService.clearSelectedTableRows();
		}

		// update when the tab is changed to update data
		this.updateTableData();
	}

	updateTableData() {
		const tabProp = this.tab['prop'];
		const tabType = this.tab['type'];
		this.tabObj = tabProp !== undefined && tabType !== undefined ? this.dataObj + '-' + tabProp + '-' + tabType : this.dataObj;
		this.columnsConfigSmall = this.configService.adjustConfigMinWidths(this.columnsConfig);
	}

	clearTableSelections(event) {
		this.dataService.clearSelectedTableRows();
	}

	dataTablePage(event) {
		if (this.tab['page'] * this.configService.pageSize > this.dataService.dataCounts[this.tabObj]) {
			console.log('NO DATA ON SELECTED PAGE');
			event.offset = 0;
		}
		this.tablePage.emit(event);
	}

	dataTableSort(event) {
		this.tableSort.emit(event);
	}

	updateTableFilter() {
		this.tab['page'] = 0;
		this.tableSearch.emit();
	}

	dataTableActivate(event) {
		if (event.type === 'click' && event.event.target.type !== 'checkbox') {
			console.log('DATA TABLE CLICK: ', event);
			this.tableClick.emit(event);
		}
	}

	dataTableSelect(event) {
		console.log('DATA TABLE SELECT: ', event);
		this.tableSelect.emit(event);
	}

	viewColumn(column) {
		return column.view;
	}

	getCellClass(column) {
		let cellClass = '';
		if (column.prop === 'contractors' || column.prop === 'queueResponse.code' || column.prop === 'reason') {
			cellClass = 'multi-cell';
		}
		if (column.prop === 'reason') {
			cellClass = 'full-cell';
		}
		return cellClass;
	}

	getRowClass(row) {
		const rowClass = {
			'alert-row': row.remainingSTPercentage <= 10
		};
		return rowClass;
	}

	expandMulti(event, rowIndex) {
		this.activeMultis = rowIndex;
	}

	collapseMulti() {
		this.activeMultis = -1;
	}

	expandCode(event, rowIndex) {
		console.log('EXPAND CODE', rowIndex);
		this.activeCode = rowIndex;
	}

	collapseCode() {
		this.activeCode = -1;
	}

	expandFullValue(value, rowIndex, columnIndex) {
		if (typeof value === 'string' && value.length > 12) {
			this.activeRow = rowIndex;
			this.activeColumn = columnIndex;
		} else {
			this.activeRow = -1;
			this.activeColumn = -1;
		}
	}

	collapseFullValue() {
		this.activeRow = -1;
		this.activeColumn = -1;
	}

	broadcastSearch(event) {
		console.log('DATA TABLE BROADCAST SEARCH', event);
		this.search.emit({
			event: event,
			data: event.data
		});
	}

	toggleCollapse() {
		this.collapsed = !this.collapsed;
	}

	shouldPage(count) {
		let shouldPage = true;
		if (count === undefined || count === '') {
			shouldPage = false;
		}
		return shouldPage;
	}

	getId(row) {
		return row[this.rowId];
	}

	getIdFunction() {
		return this.getId.bind(this);
	}

	getTableJSON() {
		let tableData = '';
		try {
			tableData = JSON.stringify(this.dataService.pageData[this.tabObj][this.pageNumber], undefined, 4);
		} catch (e) {}
		return tableData;
	}

	getAllSearchableFields = () => {
		return this.searchService.getAllSearchableFields(this.dataObj);
	}

	/**
	 * Get table filters object for the current table and tab
	 */
	getTableFilterObject() {
		if (this.tab.tableFilters === undefined) {
			this.tab.tableFilters = [];
		}
		return this.tab.tableFilters;
	}

	setRowsPerPage(event) {
		event.data = {
			rowsPerPage: this.rowsPerPage,
		};
		this.tableRefresh.emit(event);
	}
}
